import { useState, useRef } from 'react'
import { postsService } from '../../services/postsService'
import WheelLoader from '../common/WheelLoader'
import type { Post } from '../../types'

interface CoverImageUploadProps {
  postId?: number
  currentImageUrl: string | null | undefined
  onUpload: (post: Post) => void
}

export default function CoverImageUpload({
  postId,
  currentImageUrl,
  onUpload,
}: CoverImageUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !postId) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!validTypes.includes(file.type)) {
      setError('Please select a JPEG, PNG, GIF, or WebP image')
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return
    }

    setUploading(true)
    setError('')
    try {
      const updatedPost = await postsService.uploadCoverImage(postId, file)
      onUpload(updatedPost)
    } catch {
      setError('Failed to upload cover image')
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRemove = async () => {
    if (!postId) return
    setUploading(true)
    setError('')
    try {
      const updatedPost = await postsService.removeCoverImage(postId)
      onUpload(updatedPost)
    } catch {
      setError('Failed to remove cover image')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden">
      {error && (
        <div className="p-3 bg-red-50 border-b border-red-100">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {currentImageUrl ? (
        <div className="relative group">
          <img src={currentImageUrl} alt="Cover" className="w-full h-48 object-cover" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium bg-white rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Change'}
            </button>
            <button
              onClick={handleRemove}
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={!postId || uploading}
          className="w-full h-32 flex flex-col items-center justify-center text-black/40 hover:text-black/60 hover:bg-black/5 transition-colors disabled:cursor-not-allowed"
        >
          {!postId ? (
            <p className="text-sm">Save post first to add cover image</p>
          ) : uploading ? (
            <div className="flex flex-col items-center">
              <WheelLoader width={48} className="mb-2" />
              <p className="text-sm">Uploading...</p>
            </div>
          ) : (
            <>
              <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-sm">Add cover image</p>
              <p className="text-xs mt-1">JPEG, PNG, GIF, or WebP up to 5MB</p>
            </>
          )}
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  )
}
